import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceOfferRedemptionType,
  useLoyaltyServiceOffersQuery,
} from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import {
  AppOffer,
  AppOfferListItem,
  AppPersonalizedOfferGroup,
  isAppOfferGroup,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';
import noop from 'utils/noop';

import { ServiceOfferItem } from '../types';
import { mapServiceOfferItem } from '../utils';
import { getAppliedOfferIds } from '../utils/applied-offer-ids';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

function isAppOffer(item: AppOfferListItem): item is AppOffer {
  return (
    (item as any).__apptype === 'AppSystemwideOffer' ||
    (item as any).__apptype === 'AppPersonalizedOffer'
  );
}

export function useLoyaltyServiceOffers({ enabled = true }: { enabled?: boolean } = {}) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();

  const loyaltyServiceMode = getLoyaltyServiceServiceMode(serviceMode);
  const variables = {
    serviceMode: loyaltyServiceMode,
    storeId,
    redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
    includeOfferGroups: true,
    omitInvalids: false,
  };
  const querySkip = !enabled || loyaltyUserIdLoading;

  const {
    data: loyaltyServiceOffersData,
    loading: loyaltyServiceOffersLoading,
    refetch: refetchLoyaltyServiceOffers,
  } = useLoyaltyServiceOffersQuery({
    variables,
    skip: querySkip,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const loading = loyaltyUserIdLoading || loyaltyServiceOffersLoading;

  const appliedOfferIds = useMemo(() => getAppliedOfferIds(appliedOffers, minimalCartEntries), [
    appliedOffers,
    minimalCartEntries,
  ]);

  const inRestaurantRedemptionIds = useMemo(() => {
    return new Set(
      inRestaurantRedemptionCart
        .filter(entry => entry.type === ICartEntryType.OFFER)
        .map(entry => (entry as any).details?.offer?._id)
        .filter(Boolean)
    );
  }, [inRestaurantRedemptionCart]);

  const isOfferApplied = useCallback(
    (offer: AppOffer) =>
      appliedOfferIds.has(offer.loyaltyEngineId) || inRestaurantRedemptionIds.has(offer.cmsId),
    [appliedOfferIds, inRestaurantRedemptionIds]
  );

  const { offerListItems, offersInCart, personalizedOfferGroups } = useMemo(() => {
    const items = loyaltyServiceOffersData?.loyaltyServiceOffers?.items;
    if (!items?.length) {
      return {
        offerListItems: [] as AppOfferListItem[],
        offersInCart: [] as AppOffer[],
        personalizedOfferGroups: [] as AppPersonalizedOfferGroup[],
      };
    }

    const mapped = items.map(item => mapServiceOfferItem(item as ServiceOfferItem));
    const list: AppOfferListItem[] = [];
    const cart: AppOffer[] = [];
    const cartOfferIds = new Set<string>();
    const pGroups: AppPersonalizedOfferGroup[] = [];

    const addAppliedOfferToCart = (offer: AppOffer) => {
      if (!isOfferApplied(offer)) {
        return false;
      }

      if (!cartOfferIds.has(offer.loyaltyEngineId)) {
        cart.push(offer);
        cartOfferIds.add(offer.loyaltyEngineId);
      }
      return true;
    };

    for (const item of mapped) {
      if (isAppOffer(item)) {
        if (!addAppliedOfferToCart(item)) {
          list.push(item);
        }
      } else {
        list.push(item);
        if (isAppPersonalizedOfferGroup(item)) {
          pGroups.push(item);
        }

        if (isAppOfferGroup(item) || isAppPersonalizedOfferGroup(item)) {
          item.offers.forEach(addAppliedOfferToCart);
        }
      }
    }

    return {
      offerListItems: list,
      offersInCart: cart,
      personalizedOfferGroups: pGroups,
    };
  }, [loyaltyServiceOffersData, isOfferApplied]);

  const refreshOffers = useCallback(() => {
    if (loyaltyUserIdLoading) {
      return;
    }
    void refetchLoyaltyServiceOffers();
  }, [loyaltyUserIdLoading, refetchLoyaltyServiceOffers]);

  const reprocessOffers = useCallback(noop, []);

  return {
    offerListItems,
    offersInCart,
    personalizedOfferGroups,
    reprocessOffers,
    refreshOffers,
    loading,
    baseOffersLoading: loading,
  };
}
